import {
	BN,
	BigNum,
	L2OrderBook,
	MMOraclePriceData,
	OraclePriceData,
	PhoenixSubscriber,
	SerumSubscriber,
} from '@drift-labs/sdk';
import { MarketId, MarketKey } from '@drift/common';
import produce from 'immer';
import { create } from 'zustand';
import { dlog } from '../dev';

// no longer used for orderbook, just for background market price refreshing
export enum MarketListeningSelection {
	DLOB_SERVER_POLLING = 'DLOB_SERVER_POLLING',
	BLOCKCHAIN = 'BLOCKCHAIN',
}

export const PREFERRED_SELECTED_MARKET_LISTENING_SELECTION =
	MarketListeningSelection.DLOB_SERVER_POLLING;

export const PREFERRED_BACKGROUND_MARKET_LISTENING_SELECTION =
	MarketListeningSelection.DLOB_SERVER_POLLING;

const DEFAULT_L2_ORDERBOOK: L2OrderBook = {
	bids: [],
	asks: [],
};

export type DerivedMarketTradeState = {
	markPrice: BigNum;
	bestBid: BigNum;
	bestAsk: BigNum;
	updateSlot: number;
};

export type MarketState = {
	marketId: MarketId;
	marketSlot?: number;
	orderbook: L2OrderBook;
	// TODO: migrate to react query
	oracle: {
		price: BigNum;
		slot: BN;
		confidence: BN;
		hasSufficientNumberOfDataPoints: boolean;
		twap?: BigNum;
		twapConfidence?: BN;
		maxPrice?: BigNum;
	};
	derivedState: DerivedMarketTradeState;
	// TODO: move to react query
	mmOracle?: {
		price: BigNum;
		slot: BN;
		confidence: BN;
		hasSufficientNumberOfDataPoints: boolean;
		isMMOracleActive?: boolean;
	};
};

type StateLookup<T> = {
	[marketKey: MarketKey]: T;
};

export type MarketSubscriptionState = {
	listeningSelection: MarketListeningSelection;
	preferredListeningSelection: MarketListeningSelection;
	lastSubscriptionChangeTime: number;
	upgradeAttemptCount: number;
};

export interface MarketStateStore {
	set: (x: (s: MarketStateStore) => void) => void;
	get: () => MarketStateStore;
	marketSubscriptionState: StateLookup<MarketSubscriptionState>;
	marketDataState: StateLookup<MarketState>;
	getDlobStateForMarket: (marketId: MarketId) => L2OrderBook;
	getMarketDataForMarket: (marketId: MarketId) => MarketState | undefined;
	getSubscriptionStateForMarket: (
		marketId: MarketId
	) => MarketSubscriptionState | undefined;
	phoenixEnabled: boolean;
	serumEnabled: boolean;
	phoenixSubscribers: {
		[marketKey: string]: PhoenixSubscriber;
	};
	serumSubscribers: {
		[marketKey: string]: SerumSubscriber;
	};
	latestUpdateSlot: number;
	getPhoenixSubscriberForMarket: (marketId: MarketId) => PhoenixSubscriber;
	getSerumSubscriberForMarket: (marketId: MarketId) => SerumSubscriber;
	getOracleDataForMarket: (
		marketId: MarketId
	) => MarketState['oracle'] | undefined;
	getMmOracleDataForMarket: (
		marketId: MarketId
	) => MarketState['mmOracle'] | undefined;
	getRawOracleDataForMarket: (
		marketId: MarketId
	) => OraclePriceData | undefined;
	getRawMmOracleDataForMarket: (
		marketId: MarketId
	) => MMOraclePriceData | undefined;
	getMarkPriceForMarket: (marketId: MarketId) => BigNum | undefined;
	getMarketHasLiquidity: (marketId: MarketId) => boolean;
	setListeningSelection: (
		marketId: MarketId,
		newSelection: MarketListeningSelection
	) => void;
	fallbackDatasource: (marketId: MarketId) => void;
	upgradeDatasource: (marketId: MarketId) => void;
}

const DEFAULT_MARKET_DATA_STATE = {};

const useMarketStateStore = create<MarketStateStore>(
	(set, get): MarketStateStore => {
		const setState = (fn: (s: MarketStateStore) => void) => set(produce(fn));

		const getMarketStateForMarket = (marketId: MarketId) => {
			if (!marketId) return undefined;

			return get().marketDataState[marketId.key];
		};

		const getSubscriptionStateForMarket = (marketId: MarketId) => {
			if (!marketId) return undefined;

			return get().marketSubscriptionState[marketId.key];
		};

		const setListeningSelection = (
			marketId: MarketId,
			newListeningSelection: MarketListeningSelection
		) => {
			setState((s) => {
				s.marketSubscriptionState[marketId.key].listeningSelection =
					newListeningSelection;
				s.marketSubscriptionState[marketId.key].lastSubscriptionChangeTime =
					Date.now();
			});
		};

		return {
			set: setState,
			get: () => get(),
			// Should default to the DLOB_SERVER .. A hook ensures that we switch to blockchain listening if the dlob server is unhealthy
			marketDataState: DEFAULT_MARKET_DATA_STATE,
			marketSubscriptionState: DEFAULT_MARKET_DATA_STATE,
			latestUpdateSlot: 0,
			getDlobStateForMarket: (marketId: MarketId) => {
				if (!marketId) {
					return DEFAULT_L2_ORDERBOOK;
				}

				const marketState = getMarketStateForMarket(marketId);

				const dlobStateForMarket = marketState?.orderbook;

				if (!dlobStateForMarket) {
					return DEFAULT_L2_ORDERBOOK;
				}

				return dlobStateForMarket;
			},
			getSubscriptionStateForMarket: (marketId: MarketId) => {
				if (!marketId) {
					return undefined;
				}

				const subscriptionState = getSubscriptionStateForMarket(marketId);

				return subscriptionState;
			},
			getMarketDataForMarket: (marketId: MarketId) => {
				const marketState = getMarketStateForMarket(marketId);

				return marketState;
			},
			getOracleDataForMarket: (marketId: MarketId) => {
				const marketState = getMarketStateForMarket(marketId);
				const oracleState = marketState?.oracle;

				return oracleState;
			},
			getMmOracleDataForMarket: (marketId: MarketId) => {
				const marketState = getMarketStateForMarket(marketId);
				const mmOracleState = marketState?.mmOracle;

				return mmOracleState;
			},
			getRawOracleDataForMarket: (marketId: MarketId) => {
				const marketState = getMarketStateForMarket(marketId);
				const oracleState = marketState?.oracle;

				if (!oracleState) return undefined;
				return {
					price: oracleState.price.val,
					slot: oracleState.slot,
					confidence: oracleState.confidence,
					hasSufficientNumberOfDataPoints:
						oracleState.hasSufficientNumberOfDataPoints,
					twap: oracleState.twap?.val,
					twapConfidence: oracleState.twapConfidence,
					maxPrice: oracleState.maxPrice?.val,
				};
			},
			getRawMmOracleDataForMarket: (marketId: MarketId) => {
				const marketState = getMarketStateForMarket(marketId);
				const mmOracleState = marketState?.mmOracle;

				if (!mmOracleState) return undefined;
				return {
					price: mmOracleState.price.val,
					slot: mmOracleState.slot,
					confidence: mmOracleState.confidence,
					hasSufficientNumberOfDataPoints:
						mmOracleState.hasSufficientNumberOfDataPoints,
					isMMOracleActive: mmOracleState.isMMOracleActive ?? false,
				};
			},
			getMarkPriceForMarket: (marketId: MarketId) => {
				const marketState = getMarketStateForMarket(marketId);
				const markPrice = marketState?.derivedState?.markPrice;

				return markPrice;
			},
			getMarketHasLiquidity: (marketId: MarketId) => {
				if (marketId.isPerp) return true;

				const marketState = getMarketStateForMarket(marketId);

				const dlobStateForMarket = marketState?.orderbook;

				// don't hide market because orderbook hasn't loaded yet
				if (!dlobStateForMarket) return true;

				const hasLiquidity =
					dlobStateForMarket.asks?.length > 0 &&
					dlobStateForMarket.bids?.length > 0;

				return hasLiquidity;
			},
			phoenixSubscribers: {},
			serumSubscribers: {},
			getPhoenixSubscriberForMarket: (marketId: MarketId) => {
				const phoenixSubscribers = get().phoenixSubscribers;
				const marketKey = marketId.key;
				return phoenixSubscribers[marketKey];
			},
			getSerumSubscriberForMarket: (marketId: MarketId) => {
				const serumSubscribers = get().serumSubscribers;
				const marketKey = marketId.key;
				return serumSubscribers[marketKey];
			},
			phoenixEnabled: true,
			serumEnabled: true,
			setListeningSelection,
			fallbackDatasource: (marketId: MarketId) => {
				const currentListeningSelection =
					getSubscriptionStateForMarket(marketId)?.listeningSelection;

				const from = currentListeningSelection;
				let to;

				switch (currentListeningSelection) {
					case MarketListeningSelection.BLOCKCHAIN: {
						to = MarketListeningSelection.DLOB_SERVER_POLLING;
						setListeningSelection(
							marketId,
							MarketListeningSelection.DLOB_SERVER_POLLING
						);
						break;
					}
					case MarketListeningSelection.DLOB_SERVER_POLLING: {
						to = MarketListeningSelection.BLOCKCHAIN;
						setListeningSelection(
							marketId,
							MarketListeningSelection.BLOCKCHAIN
						);
						break;
					}
				}

				dlog(
					`market_subscriptions`,
					`Falling Back Subscription for ${marketId.key} .. ${from}=>${to}`
				);
			},
			upgradeDatasource: (marketId: MarketId) => {
				/**
				 * The purpose of this method is to try set the listening selection to the preferred datasource.
				 */

				const currentSubscriptionStateForMarket =
					getSubscriptionStateForMarket(marketId);

				if (!currentSubscriptionStateForMarket) {
					setState((s) => {
						s.marketSubscriptionState[marketId.key] = {
							upgradeAttemptCount: 0,
							lastSubscriptionChangeTime: Date.now(),
							listeningSelection:
								PREFERRED_BACKGROUND_MARKET_LISTENING_SELECTION,
							preferredListeningSelection:
								PREFERRED_BACKGROUND_MARKET_LISTENING_SELECTION,
						};
					});
					return;
				}

				const currentListeningSelection =
					currentSubscriptionStateForMarket.listeningSelection;
				const preferredListeningSelection =
					currentSubscriptionStateForMarket.preferredListeningSelection;

				if (currentListeningSelection === preferredListeningSelection) {
					// Already listening to the preferred datasource, so just reset the lastSubscriptionChangeTime to avoid fall-up triggering again soon
					setState((s) => {
						s.marketSubscriptionState[marketId.key].lastSubscriptionChangeTime =
							Date.now();
					});
					return;
				}

				dlog(
					`market_subscriptions`,
					`Upgrading Subscription for ${marketId.key} .. ${currentListeningSelection}=>${preferredListeningSelection}`
				);

				setState((s) => {
					s.marketSubscriptionState[marketId.key].upgradeAttemptCount =
						s.marketSubscriptionState[marketId.key].upgradeAttemptCount + 1;
					s.marketSubscriptionState[marketId.key].listeningSelection =
						preferredListeningSelection;
					s.marketSubscriptionState[marketId.key].lastSubscriptionChangeTime =
						Date.now();
				});
			},
		};
	}
);

export default useMarketStateStore;
